import { useState, useEffect, useRef } from 'react';
import { filterTypes } from '../../config';
import { formatCategory, customStartCase } from '../../util';
import isEqual from 'lodash.isequal';
import { useConfig } from "../../contexts/ConfigContext";

const ProductNav = ({
    selectedFilters,
    filterTerms,
    sort,
    setSort,
    display,
    setDisplayStyle,
    initialDisplayStyle,
    searchQuery,
}) => {
    const {
        menuFilter,
        menuConfig = {}
    } = useConfig();
    
    const { specialsName } = menuConfig;
    const safeSpecialsName = specialsName || 'Deals';
    const [title, setTitle] = useState('All');
    const [showDropdown, setShowDropdown] = useState(false);
    const [showStyleDropdown, setShowStyleDropdown] = useState(false);
    const [isMobileList, setIsMobileList] = useState(() => window.innerWidth < 768);
    const [userSelectedStyle, setUserSelectedStyle] = useState(false);
    const [lastWidth, setLastWidth] = useState(() => window.innerWidth);
    const [currentDisplayStyle, setCurrentDisplayStyle] = useState(() => {
        const isMobile = window.innerWidth < 768;
        if (isMobile) {
            return 'list';
        }
        return initialDisplayStyle || 'card';
    });
    

    const sortKeys = {
        name: 'NAME',
        popular: 'POPULAR',
        price: 'PRICE',
        potency: 'POTENCY',
    };

    const sortDirs = {
        asc: 'ASC',
        desc: 'DESC',
    };

    const getSortButtonText = (key) => {
        switch (key) {
            case 'POPULAR':
                return 'Popular';
            case 'NAME':
                return 'Name';
            case 'PRICE':
                return sort.sortDir === sortDirs.asc
                    ? 'Price: Low to High'
                    : 'Price: High to Low';
            case 'POTENCY':
                return sort.sortDir === sortDirs.asc
                    ? 'Potency: Low to High'
                    : 'Potency: High to Low';
            default:
                return 'Sort By';
        }
    };

    /**
     * Title.
     */
    useEffect(() => {
        if (isEqual(selectedFilters, menuFilter)) {
            setTitle('All');
        } else if (selectedFilters[filterTypes.specials]) {
            if (selectedFilters[filterTypes.category]) {
                setTitle(
                    `${safeSpecialsName} - ${formatCategory(
                        selectedFilters[filterTypes.category]
                    )}`
                );
            } else if (selectedFilters[filterTypes.brands]?.length > 0) {
                const selectedBrandId = selectedFilters[filterTypes.brands][0];
                const selectedBrand = filterTerms.brands.find(
                    (brand) => brand.id === selectedBrandId
                );

                setTitle(
                    `${safeSpecialsName} - ${selectedBrand?.name
                        ? selectedBrand.name
                        : 'Brand Results'
                    }`
                );
            } else {
                setTitle(safeSpecialsName);
            }
        } else if (selectedFilters[filterTypes.category]) {
            setTitle(
                `${formatCategory(selectedFilters[filterTypes.category])}`
            );
        } else if (selectedFilters[filterTypes.brands]?.length > 0) {
            const selectedBrandId = selectedFilters[filterTypes.brands][0];
            const selectedBrand = filterTerms.brands.find(
                (brand) => brand.id === selectedBrandId
            );

            if (selectedBrand?.name) {
                setTitle(`${selectedBrand.name}`);
            } else {
                setTitle(`Brand Results`);
            }
        } else if (selectedFilters[filterTypes.staff_picks]) {
            setTitle('Staff Picks');
        } else if (selectedFilters[filterTypes.effects]?.length > 0) {
            console.log(selectedFilters[filterTypes.effects]);
            const formattedEffects = selectedFilters[filterTypes.effects].map(effect => customStartCase(effect)).join(', ');
            setTitle(formattedEffects);
        } else if (selectedFilters[filterTypes.subcategory]) {
            setTitle(
                customStartCase(
                    selectedFilters[filterTypes.subcategory].toLowerCase()
                )
            );
        } else if (selectedFilters.search) {
            setTitle('');  // Clear title when searching so "Searching for..." shows
        } else {
            setTitle('');
        }
    }, [selectedFilters]);

    /**
     * Sorting dropdown.
     */

    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (
                showDropdown &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [showDropdown]);

    /**
     * Layout/style dropdown.
     */
    const styleDropdownRef = useRef(null);
    const styleButtonRef = useRef(null);

    useEffect(() => {
        const handleDocumentClick = (event) => {
            if (
                showStyleDropdown &&
                !styleDropdownRef.current.contains(event.target) &&
                !styleButtonRef.current.contains(event.target)
            ) {
                setShowStyleDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleDocumentClick);

        return () => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [showStyleDropdown]);

    /**
     * Set display style to list on mobile by default.
     */
        
    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            const wasMobile = lastWidth < 768;
            setIsMobileList(isMobile);
            setLastWidth(window.innerWidth);
    
            if (isMobile !== wasMobile) {
                setUserSelectedStyle(null);
            }
    
            let newDisplay;
            if (isMobile) {
                newDisplay = userSelectedStyle || 'list';
            } else {
                newDisplay = userSelectedStyle || initialDisplayStyle || 'card';
            }
    
            setCurrentDisplayStyle(newDisplay);
            setDisplayStyle(newDisplay);
        };
    
        // Set the initial display style on mount
        const isMobile = window.innerWidth < 768;
        const initialDisplay = isMobile ? 'list' : (initialDisplayStyle || 'card');
        if (currentDisplayStyle !== initialDisplay && !userSelectedStyle) {
            setCurrentDisplayStyle(initialDisplay);
            setDisplayStyle(initialDisplay);
        }
    
        window.addEventListener('resize', handleResize);
    
        return () => window.removeEventListener('resize', handleResize);
    }, [setDisplayStyle, initialDisplayStyle, userSelectedStyle, lastWidth, currentDisplayStyle]);

    return (
        <div className="headless-products-nav">
            {title?.length > 0 && !selectedFilters.search && (
                <h2 className="headless-products-nav--title">
                    <span className="products-nav-title-span headless-font-normal">Shop</span> {title}
                </h2>
            )}
            {(searchQuery || selectedFilters.search) && (
                <h2 className="headless-products-nav--title">
                    <span className="products-nav-title-span headless-font-normal">Searching for</span>{' '}
                    {selectedFilters.search || searchQuery}
                </h2>
            )}
            <div className="headless-products-nav--buttonwrapper">
                <div className="headless-products-nav--sort">
                    <button
                        ref={buttonRef}
                        onClick={() => setShowDropdown(!showDropdown)}
                        className={`headless-products-nav-dropdown-btn`}
                    >
                        <span>{getSortButtonText(sort.sortKey)}</span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            className="products-nav-dropdown-caret"
                        >
                            <path d="M212.7 331.3c6.2 6.2 16.4 6.2 22.6 0l160-160c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4 75.3 148.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l160 160z" />
                        </svg>
                    </button>
                    <ul
                        ref={dropdownRef}
                        className="headless-sort-dropdown"
                        style={
                            showDropdown
                                ? {}
                                : { opacity: 0, pointerEvents: 'none' }
                        }
                    >
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortDir: sortDirs.desc,
                                    sortKey: sortKeys.popular,
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Popular
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortKey: sortKeys.name,
                                    sortDir: sort.sortKey === sortKeys.name && sort.sortDir === sortDirs.asc
                                        ? sortDirs.desc
                                        : sortDirs.asc, // Default to ascending when first clicked
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Name
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortKey: sortKeys.price,
                                    sortDir: sortDirs.asc,
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Price: Low to High
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortKey: sortKeys.price,
                                    sortDir: sortDirs.desc,
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Price: High to Low
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortKey: sortKeys.potency,
                                    sortDir: sortDirs.asc,
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Potency: Low to High
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setSort({
                                    sortKey: sortKeys.potency,
                                    sortDir: sortDirs.desc,
                                });
                                setShowDropdown(false);
                            }}
                        >
                            Potency: High to Low
                        </li>
                    </ul>
                </div>
                <div className="headless-products-nav--sort headless-products-nav--style">
                    <button
                        ref={styleButtonRef}
                        onClick={() => setShowStyleDropdown(!showStyleDropdown)}
                        className={`headless-products-nav-dropdown-btn`}
                    >
                        {currentDisplayStyle === 'list' ? (
                            <>
                                <span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 448 512"
                                        className="products-nav-dropdown-style"
                                    >
                                        <path d="M0 64H448v64H0V64zM0 224H448v64H0V224zM448 384v64H0V384H448z" />
                                    </svg>
                                    List
                                </span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="products-nav-dropdown-caret"
                                >
                                    <path d="M212.7 331.3c6.2 6.2 16.4 6.2 22.6 0l160-160c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4 75.3 148.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l160 160z" />
                                </svg>
                            </>
                        ) : (
                            <>
                                <span>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="products-nav-dropdown-style"
                                    >
                                        <path d="M224 32H32V224H224V32zm0 256H32V480H224V288zM288 32V224H480V32H288zM480 288H288V480H480V288z" />
                                    </svg>
                                    Card
                                </span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="products-nav-dropdown-caret"
                                >
                                    <path d="M212.7 331.3c6.2 6.2 16.4 6.2 22.6 0l160-160c6.2-6.2 6.2-16.4 0-22.6s-16.4-6.2-22.6 0L224 297.4 75.3 148.7c-6.2-6.2-16.4-6.2-22.6 0s-6.2 16.4 0 22.6l160 160z" />
                                </svg>
                            </>
                        )}
                    </button>
                    <ul
                        ref={styleDropdownRef}
                        className="headless-sort-dropdown"
                        style={
                            showStyleDropdown
                                ? {}
                                : { opacity: 0, pointerEvents: 'none' }
                        }
                    >
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setCurrentDisplayStyle('list');
                                setDisplayStyle('list');
                                setUserSelectedStyle('list');
                                setShowStyleDropdown(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                                className="products-nav-dropdown-style"
                            >
                                <path d="M0 64H448v64H0V64zM0 224H448v64H0V224zM448 384v64H0V384H448z" />
                            </svg>
                            List
                        </li>
                        <li
                            style={{
                                listStyle: 'none',
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                setCurrentDisplayStyle('card');
                                setDisplayStyle('card');
                                setUserSelectedStyle('card');
                                setShowStyleDropdown(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="products-nav-dropdown-style"
                            >
                                <path d="M224 32H32V224H224V32zm0 256H32V480H224V288zM288 32V224H480V32H288zM480 288H288V480H480V288z" />
                            </svg>
                            Card
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ProductNav;
